import { useRef, useState } from 'react';
import { Box, Button, Typography, Stack } from '@mui/material';
import { DesktopLayout, WithoutHeader } from 'src/layout';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import UserSevice from 'src/services/UserSevice';
import ChangeEmail, { TypeChangeEmailScheme } from 'src/validations/changeEmail';

import useUserStore from 'src/stores/user';
import LoginField from 'src/components/LoginField';
import { useSnackbar } from 'notistack';
import OtpField from 'src/components/OtpField';
import SendCodeAgain from 'src/widgets/SendCodeAgain';
import { history } from 'src/utils/history';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import useSessionStore from 'src/stores/session';
import FormWrapper from 'src/components/FormWrapper';

const ChangeEmailScreen = () => {
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useIsMobileSize();
    const { isAuthorized } = useSessionStore(({ isAuthorized }) => ({ isAuthorized }));
    const ref = useRef<HTMLButtonElement | null>(null);
    const { setEmail } = useUserStore(({ setEmail }) => ({ setEmail }));
    const [confirmedEmail, setConfirmedEmail] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const currentEmail = useUserStore((s) => s.email);

    const {
        control,
        handleSubmit,
        getValues,
        formState: { errors, isValid },
    } = useForm<TypeChangeEmailScheme>({
        resolver: zodResolver(ChangeEmail),
    });

    const handleRefetchCode = () => {
        if (isValid) {
            const email = getValues('new_email');
            fetchChangeEmail({ new_email: email });
        }
    };

    const handleComplete = () => {
        if (ref.current) ref.current.click();
    };

    const fetchChangeEmail = (data) => {
        setIsLoading(true);

        UserSevice.changeEmail({
            new_email: data.new_email,
        })
            .then(() => {
                setConfirmedEmail(true);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                enqueueSnackbar('Не удалось сменить email', { variant: 'error' });
            });
    };

    const onSubmit = (data: TypeChangeEmailScheme) => {
        if (data.code) {
            setIsLoading(true);
            UserSevice.changeEmailConfirm({
                new_email: data.new_email,
                code: data.code,
            })
                .then(() => {
                    setIsLoading(false);

                    enqueueSnackbar('Email успешно изменен', { variant: 'success' });

                    setEmail(data.new_email);

                    history.push('/');
                })
                .catch(() => {
                    setIsLoading(false);
                    enqueueSnackbar('Не удалось сменить email. Неверный код подтверждения', { variant: 'error' });
                });

            return;
        }

        fetchChangeEmail(data);
    };

    const renderContent = () => {
        return (
            <form onSubmit={handleSubmit(onSubmit)}>
                {!confirmedEmail && (
                    <Box mb={2}>
                        <Typography
                            variant="body2"
                            color="secondary"
                            mb={1}
                        >
                            Текущий email: {currentEmail}
                        </Typography>
                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="new_email"
                                sx={{ fontWeight: 600 }}
                            >
                                Новый email
                            </Typography>
                            <Controller
                                name="new_email"
                                control={control}
                                render={({ field }) => (
                                    <LoginField
                                        {...field}
                                        label={undefined}
                                        placeholder="Введите новую почту"
                                        error={errors?.new_email?.message}
                                    />
                                )}
                            />
                        </Stack>
                    </Box>
                )}
                {confirmedEmail && (
                    <>
                        <Box mb={2}>
                            <Controller
                                name="code"
                                control={control}
                                render={({ field }) => (
                                    <OtpField
                                        {...field}
                                        onComplete={handleComplete}
                                    />
                                )}
                            />
                        </Box>
                        <Box mb={0.5}>
                            <SendCodeAgain
                                timer={30}
                                onClick={handleRefetchCode}
                            />
                        </Box>
                    </>
                )}
                <Box>
                    <Button
                        ref={(r) => (ref.current = r)}
                        type="submit"
                        variant="contained"
                        size="large"
                        color="primary"
                        disabled={isLoading}
                        fullWidth
                    >
                        Изменить почту
                    </Button>
                </Box>
            </form>
        );
    };

    if (isAuthorized && !isMobile)
        return (
            <DesktopLayout hideRightSidebar>
                <Box sx={{ maxWidth: 600 }}>
                    <FormWrapper
                        name="Изменить почту"
                        topGutter
                    >
                        {renderContent()}
                    </FormWrapper>
                </Box>
            </DesktopLayout>
        );

    return (
        <WithoutHeader
            showBack
            showMyHeader
            title="Изменить почту"
        >
            {renderContent()}
        </WithoutHeader>
    );
};

export default ChangeEmailScreen;
